import { supabase } from '@/integrations/supabase/client';

const API_BASE = import.meta.env.VITE_API_URL as string;

if (!API_BASE) {
  console.warn('[api/client] VITE_API_URL is not set — API calls will fail.');
}

/**
 * Base fetch wrapper for all MoldOp API calls.
 *
 * - Automatically attaches the Supabase JWT as Bearer token.
 * - Throws on non-2xx responses with the server's error message.
 * - T is the expected response body type.
 */
export async function apiFetch<T>(
  path: string,
  init?: RequestInit,
): Promise<T> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session?.access_token ?? ''}`,
      ...(init?.headers ?? {}),
    },
  });

  if (!res.ok) {
    let message = `API error ${res.status}`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      // response body wasn't JSON — keep the default message
    }
    throw new Error(message);
  }

  // 204 No Content — return undefined cast to T
  if (res.status === 204) return undefined as T;

  return res.json() as Promise<T>;
}

/** Convenience wrappers */
export const apiGet = <T>(path: string) =>
  apiFetch<T>(path, { method: 'GET' });

export const apiPost = <T>(path: string, body: unknown) =>
  apiFetch<T>(path, { method: 'POST', body: JSON.stringify(body) });

export const apiPatch = <T>(path: string, body: unknown) =>
  apiFetch<T>(path, { method: 'PATCH', body: JSON.stringify(body) });

export const apiDelete = <T>(path: string) =>
  apiFetch<T>(path, { method: 'DELETE' });
